import { useState } from "react";
import { Share2, Link, Mail, MessageCircle, Facebook, X } from "lucide-react";
import { toast } from "sonner";

export default function ShareButton({ title = "Awaken India Tours", description = "10-Day Luxury Journey to India" }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentUrl = window.location.href;
  const shareText = `${title} - ${description}`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success("Link copied to clipboard!");
      setIsOpen(false);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };
  
  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedText = encodeURIComponent(shareText);
    
    const shareUrls = {
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this amazing India tour!\n\n${description}\n\n${currentUrl}`)}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    };
    
    window.open(shareUrls[platform], "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  // Use native share if available on mobile
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: currentUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative" data-testid="share-button-container">
      {/* Main Share Button */}
      <button
        onClick={handleNativeShare}
        className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full transition-all duration-300 border border-white/20 backdrop-blur-sm text-xs"
        data-testid="share-button"
        aria-label="Share this page"
      >
        <Share2 size={14} />
        <span className="font-medium">Share</span>
      </button>

      {/* Share Options Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div 
            className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in-up"
            data-testid="share-dropdown"
          >
            <div className="p-3 bg-[#243b5a] text-white">
              <p className="text-sm font-medium">Share this trip</p>
              <p className="text-xs text-white/70 truncate">{title}</p>
            </div>
            
            <div className="p-2">
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                data-testid="share-copy-link"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Link size={16} className="text-gray-600" />
                </div>
                <span className="text-sm">Copy Link</span>
              </button>
              
              <button
                onClick={() => handleShare("email")}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                data-testid="share-email"
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail size={16} className="text-blue-600" />
                </div>
                <span className="text-sm">Email</span>
              </button>
              
              <button
                onClick={() => handleShare("whatsapp")}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                data-testid="share-whatsapp"
              >
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <MessageCircle size={16} className="text-green-600" />
                </div>
                <span className="text-sm">WhatsApp</span>
              </button>
              
              <button
                onClick={() => handleShare("facebook")}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                data-testid="share-facebook"
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Facebook size={16} className="text-blue-700" />
                </div>
                <span className="text-sm">Facebook</span>
              </button>
              
              <button
                onClick={() => handleShare("twitter")}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                data-testid="share-twitter"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <X size={16} className="text-gray-800" />
                </div>
                <span className="text-sm">X (Twitter)</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
